'use client'

import { Cart, CartItem, ItemTypes, Merchant, PaymentSubType, PaymentType, Rate } from '@/app/(commerce)/_lib/commerce'
import { logger } from '@/lib/logger'
import { sumBy } from 'lodash'
import nookies from 'nookies'
import { create } from 'zustand'
import { persist, subscribeWithSelector } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

const CART_KEY_LOCAL = 'mycart'
const CART_KEY_COOKIE = '.cartKey'
// Device/browser session ID for cart persistence across login/logout
// This survives logout and is tied to the device, not the user
const CART_SESSION_KEY = 'cart-session-id'
// Empty GUID that indicates an invalid/uninitialized cart key
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'

/**
 * Get or create a persistent device session ID.
 * This ID is used to associate carts with a specific device/browser,
 * allowing carts to persist across login/logout on the same device.
 *
 * Related: PL-500 - Skyward Food Services add to cart 404 error
 */
const getOrCreateSessionId = (): string => {
  if (typeof localStorage === 'undefined') {
    // SSR fallback - generate a temporary ID
    return crypto.randomUUID()
  }

  let sessionId = localStorage.getItem(CART_SESSION_KEY)
  if (!sessionId) {
    sessionId = crypto.randomUUID()
    localStorage.setItem(CART_SESSION_KEY, sessionId)
    logger.info('Created new cart session ID', { sessionId })
  }
  return sessionId
}

// Helper to check if a cart key is valid (not null, undefined, empty string, or empty GUID)
const isValidCartKey = (key: string | null | undefined): key is string => {
  return !!key && key !== 'null' && key !== 'undefined' && key !== EMPTY_GUID
}

// Types
export type CartAction = 'remove-cart-item' | 'add-cart-item' | 'cart-items'

export type AddCartItem = {
  integrationId?: number
  feeId?: number
  fee_bcId?: number
  name: string
  description: string
  studentId?: number | string
  schoolId: number
  amount: number
  itemType: number
  cartKey: string
  studentName?: string
  recurringFrequency?: string
  firstRecurDate?: string
  lowBalanceRecur?: number
}
export type RemoveCartItem = {
  id: number
  key: string
}
/*
"id": 17097887,
"key": "73815c55-c4c3-416e-88bd-d783a4452218",
"sessionId": "450fc6fc-47ff-4732-9ed8-cbe077b4e096",
"merchant": null,
"rate": {
    "paymentMethodType": "None",
    "merchantPays": false,
    "feePerTransaction": 0,
    "feePerItem": 0,
    "percentPerTransaction": 0,
    "taxPercentage": 0
},
"tenantId": null,
"schoolId": null,
"departmentId": null,
"paymentType": "None",
"paymentSubType": null,
"items": [],
"totalItems": 0,
"total": 0,
"tax": 0,
"subtotal": 0,
"fees": 0,
"discount": 0
*/
export type CartState = {
  // Cart key management
  id: number | undefined
  key: string | undefined
  sessionId: string | undefined
  merchant: Merchant | null
  // Cart items - using Map for efficient lookups (from context store)
  items: CartItem[]
  rate: Rate
  tenantId: number | null
  schoolId: number | null
  departmentId: number | null
  paymentType: PaymentType
  paymentSubType: PaymentSubType | null
  totalItems: number
  total: number
  tax: number
  subtotal: number
  fees: number
  discount: number
  // Additional computed properties from cart-slice

  // Loading and error states
  error: string | null
  // Track specific loading states
  loadingStates: {
    fetch: boolean
    add: boolean
    remove: boolean
    payment: boolean
  }
  // Request cancellation
  abortController: AbortController | null
  // Hydration state - true after store has rehydrated from localStorage
  _hasHydrated: boolean
}

export type CartActions = {
  // Cart key management (from context store)
  reset: () => void
  setCartKey: (key: string) => void
  getCartKey: () => string | undefined
  setCart: (cart: Cart) => void

  // Session ID management (device-based cart persistence)
  // Related: PL-500 - Skyward Food Services add to cart 404 error
  initSession: () => string
  getSessionId: () => string

  setError: (error: string | null) => void
  setLoadingState: (key: keyof CartState['loadingStates'], loading: boolean) => void
  // Hydration setter
  setHasHydrated: (hasHydrated: boolean) => void
}

export type CartStore = CartState & CartActions

// Helper functions
export const total = (items: CartItem[]) => sumBy(items, (s) => s.amount ?? 0.0) ?? 0.0
export const totalItems = (items: CartItem[]) => items?.length ?? 0
export const hasSkyward = (items: CartItem[]) =>
  items?.some((x) => x.itemTypeId === ItemTypes.SkywardFee || x.itemTypeId === ItemTypes.SkywardFoodService) ?? false
export const hasFeePayment = (items: CartItem[]) => items?.some((x) => x.itemTypeId === ItemTypes.Fee) ?? false
export const hasRecurring = (items: CartItem[]) => items?.some((x) => x.meta?.recurring) ?? false
export const hasFoodPayment = (items: CartItem[]) => items?.some((x) => x.itemTypeId === ItemTypes.SkywardFee) ?? false
export const hasSameOrg = (items: CartItem[]) => items.every((x) => x.tenantId === items[0]?.tenantId) ?? false

export const getCartProperties = (items: CartItem[]) => {
  return {
    cartItems: items,
    total: total(items),
    totalItems: totalItems(items),
    hasSkyward: hasSkyward(items),
    hasFeePayment: hasFeePayment(items),
    hasRecurring: hasRecurring(items),
    hasFoodPayment: hasFoodPayment(items),
  }
}

const initialState: CartState = {
  key: undefined,
  id: undefined,
  sessionId: undefined,
  merchant: null,
  items: [],
  error: null,
  loadingStates: {
    fetch: false,
    add: false,
    remove: false,
    payment: false,
  },
  rate: {
    paymentMethodType: 'None',
    merchantPays: false,
    feePerTransaction: 0,
    feePerItem: 0,
    percentPerTransaction: 0,
    taxPercentage: 0,
  },
  tenantId: null,
  schoolId: null,
  departmentId: null,
  paymentType: 'None',
  paymentSubType: null,
  totalItems: 0,
  total: 0,
  tax: 0,
  subtotal: 0,
  fees: 0,
  discount: 0,
  abortController: null,
  _hasHydrated: false,
}

export const useCartStore = create<CartStore>()(
  persist(
    subscribeWithSelector(
      immer((set, get) => ({
        ...initialState,

        reset: () => {
          // Preserve the sessionId across reset (device-based persistence)
          // Related: PL-500 - Cart should persist on same device across login/logout
          const currentSessionId = get().sessionId

          // Clear cart key from localStorage (but NOT session ID)
          try {
            if (typeof localStorage !== 'undefined') {
              localStorage.removeItem(CART_KEY_LOCAL)
              // Note: We do NOT remove CART_SESSION_KEY - it's device-based
            }
          } catch (error) {
            logger.error('Error clearing cart key from local storage', { error })
          }

          try {
            nookies.destroy(null, CART_KEY_COOKIE)
          } catch (error) {
            logger.error('Error clearing cart key from cookies', { error })
          }

          // Reset to initial state but preserve sessionId
          set({
            ...initialState,
            sessionId: currentSessionId,
          })
        },

        // Initialize or retrieve the device session ID
        // Call this before creating carts or adding items
        initSession: () => {
          const sessionId = getOrCreateSessionId()
          set((state) => {
            state.sessionId = sessionId
          })
          return sessionId
        },

        // Get the current session ID, initializing if needed
        getSessionId: () => {
          const state = get()
          if (state.sessionId) {
            return state.sessionId
          }
          // Initialize and return
          const sessionId = getOrCreateSessionId()
          set((s) => {
            s.sessionId = sessionId
          })
          return sessionId
        },

        clearCart: () => {
          set((state) => {
            state.items = []
            // Keep the cart key but clear the items
          })
        },

        // Loading and error state management

        setError: (error: string | null) => {
          set((state) => {
            state.error = error
          })
        },

        setLoadingState: (key: keyof CartState['loadingStates'], loading: boolean) => {
          set((state) => {
            state.loadingStates[key] = loading
          })
        },

        setHasHydrated: (hasHydrated: boolean) => {
          set((state) => {
            state._hasHydrated = hasHydrated
          })
        },

        setCartKey: (key: string) => {
          if (!isValidCartKey(key)) {
            logger.error('Invalid cart key provided to setCartKey', { key })
            return
          }

          logger.info('Cart key setting...', { key })
          set((state) => {
            state.key = key
          })
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem(CART_KEY_LOCAL, key)
          }
          try {
            nookies.set(null, CART_KEY_COOKIE, key, { path: '/' })
          } catch (error) {
            logger.error('Error setting cart key in cookies', { error })
          }
          logger.info('Cart key set', { key })
        },
        setCart: (cart: Cart) => {
          set((state) => {
            // Update all cart properties
            state.id = cart.id
            state.key = cart.key
            state.sessionId = cart.sessionId
            state.merchant = cart.merchant || null
            state.items = cart.items || []
            state.rate = cart.rate
            state.tenantId = cart.tenantId
            state.schoolId = cart.schoolId ?? null
            state.departmentId = cart.departmentId ?? null
            state.paymentType = (cart.paymentType as PaymentType) || 'None'
            state.paymentSubType = (cart.paymentSubType as PaymentSubType) || null
            state.totalItems = cart.totalItems || cart.items?.length || 0
            state.total = cart.total || 0
            state.tax = cart.tax || 0
            state.subtotal = cart.subtotal || 0
            state.fees = cart.fees || 0
            state.discount = cart.discount || 0

            state.error = null
            state.loadingStates.fetch = false

            logger.info('Cart set', { cartItems: cart.items?.length, total: cart.total })
          })
          if (typeof localStorage !== 'undefined' && cart.key) {
            localStorage.setItem(CART_KEY_LOCAL, cart.key)
          }
        },
        getCartKey: () => {
          // IMPORTANT: Check localStorage['mycart'] FIRST to sync with legacy pages
          // This prevents race conditions where Zustand persist loads a stale key
          // before the legacy cart key is available
          if (typeof localStorage !== 'undefined') {
            const legacyKey = localStorage.getItem(CART_KEY_LOCAL)
            // Use isValidCartKey to filter out empty GUIDs
            if (isValidCartKey(legacyKey)) {
              // NOTE: We intentionally do NOT call set() here during getCartKey()
              // because this getter is called during render and calling set() during
              // render causes "Cannot update a component while rendering" React error.
              // The sync from legacy localStorage happens in onRehydrateStorage instead.
              return legacyKey
            }
          }

          // Fallback to store state if no legacy key exists
          const state = get()
          // Validate store key as well
          if (isValidCartKey(state.key)) {
            return state.key
          }

          return undefined
        },
        hasSkyward: () => {
          const items = get().items
          return hasSkyward(items)
        },
        hasFeePayment: () => {
          const items = get().items
          return hasFeePayment(items)
        },
        hasRecurring: () => {
          const items = get().items
          return hasRecurring(items)
        },
        hasFoodPayment: () => {
          const items = get().items
          return hasFoodPayment(items)
        },
        isLoading: () => {
          const state = get()
          return (
            state.loadingStates.fetch ||
            state.loadingStates.add ||
            state.loadingStates.remove ||
            state.loadingStates.payment
          )
        },
      })),
    ),
    {
      name: 'cart-store',
      // On rehydration, sync with legacy localStorage 'mycart' key and initialize session
      onRehydrateStorage: () => (state) => {
        if (state && typeof localStorage !== 'undefined') {
          // Initialize device session ID on hydration
          // This ensures sessionId is always available for cart operations
          const sessionId = getOrCreateSessionId()
          if (!state.sessionId) {
            logger.info('Initializing session ID on hydration', { sessionId })
            state.initSession()
          }

          const legacyCartKey = localStorage.getItem(CART_KEY_LOCAL)
          // If legacy cart key is valid and differs from stored state, use the legacy key
          // Use isValidCartKey to filter out empty GUIDs
          if (isValidCartKey(legacyCartKey)) {
            if (state.key !== legacyCartKey) {
              logger.info('Syncing cart key from legacy localStorage', {
                storedKey: state.key,
                legacyKey: legacyCartKey,
              })
              state.setCartKey(legacyCartKey)
            }
          } else if (legacyCartKey === EMPTY_GUID) {
            // Clear invalid empty GUID from localStorage
            logger.info('Clearing invalid empty GUID from legacy localStorage')
            localStorage.removeItem(CART_KEY_LOCAL)
          }
          // Mark store as hydrated after all syncing is complete
          state.setHasHydrated(true)
          logger.info('Cart store hydrated', { cartKey: state.getCartKey(), sessionId: state.sessionId })
        } else if (state) {
          // Mark as hydrated even if localStorage isn't available (SSR fallback)
          state.setHasHydrated(true)
          logger.info('Cart store hydrated (no localStorage)')
        }
      },
    },
  ),
)

// Core selectors
export const useCartKey = () => useCartStore((state) => state.key)
export const useCartItems = () => useCartStore((state) => state.items)
export const useCartLoading = () =>
  useCartStore(
    (state) =>
      state.loadingStates.fetch || state.loadingStates.add || state.loadingStates.remove || state.loadingStates.payment,
  )
export const useCartError = () => useCartStore((state) => state.error)
// Hydration state selector - use this to wait for store to hydrate before making API calls
export const useCartHasHydrated = () => useCartStore((state) => state._hasHydrated)

// Actions selector
export const useCartStoreActions = () => {
  const store = useCartStore()
  return {
    reset: store.reset,
    setCartKey: store.setCartKey,
    getCartKey: store.getCartKey,
    setCart: store.setCart,
    setError: store.setError,
    setLoadingState: store.setLoadingState,
    // Session ID management (device-based cart persistence)
    initSession: store.initSession,
    getSessionId: store.getSessionId,
  }
}

// Totals selector (used in checkout)
export const useCartTotals = () => {
  return {
    subtotal: useCartStore((state) => state.subtotal),
    tax: useCartStore((state) => state.tax),
    discount: useCartStore((state) => state.discount),
    total: useCartStore((state) => state.total),
  }
}

// Generic filter selector for cart items by type (kept for tests)
export const useCartItemsByType = (itemType: keyof typeof ItemTypes) =>
  useCartStore((state) => state.items.filter((item) => item.itemTypeId === ItemTypes[itemType]))

// For backwards compatibility in tests - can be removed later
export const useCartTotal = () => useCartStore((state) => state.total)
